import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

export interface PlayerApi {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  renderer: THREE.WebGLRenderer;
  loadFromURL: (url: string) => Promise<void>;
  frame: () => void;
  toggleGrid: () => void;
  onResize: () => void;
  setEnvironment: (url: string) => Promise<void>;
  dispose: () => void;
  mixer: THREE.AnimationMixer | null;
  knipperAction: THREE.AnimationAction | null;
}

export function createPlayer(viewportEl: HTMLElement, statusEl: HTMLElement | null): PlayerApi {
  // Renderer
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(viewportEl.clientWidth, viewportEl.clientHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  viewportEl.appendChild(renderer.domElement);

  // HUD: FPS (hidden until model is loaded)
  const hud = document.createElement('div');
  hud.className = 'hud';
  const fpsChip = document.createElement('div');
  fpsChip.className = 'chip';
  fpsChip.style.display = 'none';
  fpsChip.textContent = 'FPS —';
  hud.appendChild(fpsChip);
  viewportEl.appendChild(hud);

  // Scene
  const scene = new THREE.Scene();
  scene.background = null;

  // Lights – Blender (Z-up) to Three.js (Y-up): pos/dir (X,Y,Z)->(X,Z,-Y)
  // Default Blender light direction is (0,0,-1) local; rotation (XYZ Euler) rotates it.
  const blenderLights: { pos: [number,number,number]; rot: [number,number,number] }[] = [
    { pos: [ 0.368,  0.615, 2.362], rot: [-45.618,  27.683,  -7.072] },
    { pos: [ 3.730, -0.352, 2.686], rot: [ 69.558,   0.000,  84.684] },
    { pos: [-1.508, -1.089, 2.209], rot: [ 62.156, -50.481, -32.069] },
    { pos: [ 4.076,  1.006, 5.904], rot: [ 37.261,   3.164, 106.940] },
  ];
  blenderLights.forEach(({ pos: [bx, by, bz], rot: [rx, ry, rz] }) => {
    const threePos = new THREE.Vector3(bx, bz, -by);
    const blenderDir = new THREE.Vector3(0, 0, -1).applyEuler(
      new THREE.Euler(
        THREE.MathUtils.degToRad(rx),
        THREE.MathUtils.degToRad(ry),
        THREE.MathUtils.degToRad(rz),
        'XYZ'
      )
    );
    const threeDir = new THREE.Vector3(blenderDir.x, blenderDir.z, -blenderDir.y);
    const l = new THREE.DirectionalLight(0xffffff, 1.0);
    l.position.copy(threePos);
    l.target.position.copy(threePos.clone().add(threeDir));
    scene.add(l);
    scene.add(l.target);
  });

  // Grid
  const grid = new THREE.GridHelper(10, 20, 0x404040, 0x303030);
  grid.visible = false;
  scene.add(grid);

  // Camera & controls
  const camera = new THREE.PerspectiveCamera(60, viewportEl.clientWidth / viewportEl.clientHeight, 0.01, 2000);
  camera.position.set(2.5, 1.5, 2.5);
  const controls = new OrbitControls(camera, renderer.domElement);

  controls.enableRotate = false;
  controls.enableZoom = false;
  controls.enablePan = false;


  // Loaders (use public decoder paths)
  const gltfLoader = new GLTFLoader();
  const draco = new DRACOLoader();
  draco.setDecoderPath('/decoders/draco/');
  gltfLoader.setDRACOLoader(draco);

  const ktx2 = new KTX2Loader();
  ktx2.setTranscoderPath('/decoders/basis/');
  ktx2.detectSupport(renderer);
  gltfLoader.setKTX2Loader(ktx2);

  const rgbeLoader = new RGBELoader();

  // State
  let current: THREE.Object3D | null = null;
  let mixer: THREE.AnimationMixer | null = null;
  let idleAction: THREE.AnimationAction | null = null;
  let knipperAction: THREE.AnimationAction | null = null;
  let disposed = false;
  let lastTime = performance.now();

  // FPS state
  let lastFpsUpdate = performance.now();
  let frameCount = 0;
  let fps = 0;
  const fpsUpdateInterval = 250; // ms

  function setStatus(msg: string) { if (statusEl) statusEl.textContent = msg; }

  function clearCurrent() {
    idleAction?.stop();
    idleAction = null;
    mixer?.stopAllAction();
    mixer = null;
    if (!current) return;
    scene.remove(current);
    current.traverse?.((obj: THREE.Object3D) => {
      const mesh = obj as THREE.Mesh;
      if ((mesh as any).isMesh) {
        (mesh.geometry as THREE.BufferGeometry | undefined)?.dispose?.();
        const mat = (mesh.material as any);
        if (Array.isArray(mat)) mat.forEach((m) => m?.dispose?.());
        else mat?.dispose?.();
      }
    });
    current = null;
    // Hide FPS until a model is loaded again
    fpsChip.style.display = 'none';
  }

  function frameObject(object: THREE.Object3D) {
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const min = box.min;

    const centerX = min.x + size.x * 0.5;
    const centerZ = min.z + size.z * 0.5;
    const waistY = min.y + size.y * 0.82;
    const target = new THREE.Vector3(centerX, waistY, centerZ);

    // Chest up (about 40% of height): the hands hang near half height and would otherwise be in view.
    const visibleHeight = size.y * 0.32;
    const fitDist = (visibleHeight / 2) / Math.tan((Math.PI * camera.fov) / 360);
    const distance = fitDist * 1.3;

    camera.near = Math.max(0.01, distance / 100);
    camera.far = Math.max(2000, distance * 10);
    camera.updateProjectionMatrix();
    controls.target.copy(target);
    camera.position.set(centerX, waistY + distance * 0.15, centerZ + distance);
    controls.update();
  }

  async function setEnvironment(url: string) {
    try {
      const texture = await rgbeLoader.loadAsync(url);
      texture.mapping = THREE.EquirectangularReflectionMapping;
      scene.environment = texture;
      if (!scene.background) scene.background = texture;
    } catch (e) {
      console.warn('Environment load failed:', e);
    }
  }

  async function loadFromURL(url: string) {
    clearCurrent();
    setStatus('Loading model…');
    try {
      const gltf: GLTF = await gltfLoader.loadAsync(url, (event: ProgressEvent<EventTarget>) => {
        const total = (event as any).total as number | undefined;
        const loaded = (event as any).loaded as number | undefined;
        if (total && loaded) {
          const pct = Math.round((loaded / total) * 100);
          setStatus(`Loading ${pct}%`);
        }
      });
      current = gltf.scene || gltf.scenes?.[0] || null;
      if (!current) throw new Error('No scene in GLTF');
      scene.add(current);
      frameObject(current);

      // Start idle + knipper animations simultaneously
      // Exporters differ in case ('idle' in the Robin models); the first clip there is a 0 s T-pose.
      const idleClip = gltf.animations?.find((a: { name: string; }) => a.name.toLowerCase() === 'idle') ?? gltf.animations?.[0] ?? null;
      if (idleClip && idleClip.name.toLowerCase() !== 'idle') {
        console.error('[player] model has no idle clip; falling back to', idleClip.name);
      }
      if (idleClip) {
        mixer = new THREE.AnimationMixer(current);
        idleAction = mixer.clipAction(idleClip);
        idleAction.setLoop(THREE.LoopRepeat, Infinity);
        idleAction.play();

        const knipperClip = gltf.animations?.find((a: { name: string; }) => a.name === 'Knipper') ?? null;
        if (knipperClip) {
          knipperAction = mixer.clipAction(knipperClip);
          knipperAction.setLoop(THREE.LoopRepeat, Infinity);
          knipperAction.play();
        }

        lastTime = performance.now();
      }

      setStatus('Loaded');
      // fpsChip.style.display = ''; // hidden
    } catch (err) {
      console.error(err);
      setStatus('Error loading model');
      // Keep a user-visible status but avoid alert popups in embedded contexts
    }
  }

  function onResize() {
    const w = viewportEl.clientWidth;
    const h = viewportEl.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }

  function toggleGrid() { grid.visible = !grid.visible; }

  let raf = 0;
  function loop() {
    if (disposed) return;
    const now = performance.now();
    const delta = (now - lastTime) / 1000;
    lastTime = now;
    mixer?.update(delta);
    controls.update();
    renderer.render(scene, camera);

    // FPS calculation (update text a few times per second)
    frameCount++;
    if (now - lastFpsUpdate >= fpsUpdateInterval) {
      fps = (frameCount * 1000) / (now - lastFpsUpdate);
      fpsChip.textContent = `FPS ${Math.round(fps)}`;
      lastFpsUpdate = now;
      frameCount = 0;
    }

    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);

  function dispose() {
    disposed = true;
    cancelAnimationFrame(raf);
    clearCurrent();
    controls.dispose();
    renderer.dispose();
    ktx2.dispose();
    (draco as any).dispose?.();
    if (renderer.domElement.parentElement === viewportEl) {
      viewportEl.removeChild(renderer.domElement);
    }
    if (hud.parentElement) hud.parentElement.removeChild(hud);
  }

  return {
    scene,
    camera,
    controls,
    renderer,
    loadFromURL,
    frame: () => current && frameObject(current),
    toggleGrid,
    onResize,
    setEnvironment,
    dispose,
    get mixer() { return mixer; },
    get knipperAction() { return knipperAction; },
  };
}
